// Sentry Analytics Provider
//
// This provider syncs analytics context (domain, deployment target, versions, environment) to Sentry
// for better error tracking and filtering. It's a "dummy" provider that doesn't
// track events but ensures Sentry has the right context.

import { setSentryAnalyticsContext } from '../../services/sentry';
import { logger } from '../../utils/core/logger';
import type { AnalyticsContext, AnalyticsEvent } from '../types';
import { BaseAnalyticsProvider } from './base';

export class SentryProvider extends BaseAnalyticsProvider {
    public readonly name = 'sentry';

    public eventFilter(_event: AnalyticsEvent): boolean {
        // This is a "dummy" provider - it doesn't track events
        // It only syncs analytics context to Sentry
        return false;
    }

    public async initialize(context: AnalyticsContext): Promise<void> {
        await super.initialize(context);

        // Set initial Sentry context with all available metadata
        setSentryAnalyticsContext(
            context.domain,
            context.deploymentTarget,
            context.apiVersion,
            context.frontendVersion,
            context.environment,
            context.gitCommitSha
        );

        logger.debug('✅ Sentry provider initialized with analytics context:', {
            domain: context.domain,
            deploymentTarget: context.deploymentTarget,
            apiVersion: context.apiVersion,
            frontendVersion: context.frontendVersion,
            environment: context.environment,
            gitCommitSha: context.gitCommitSha,
        });
    }

    public override updateContext(context: Partial<AnalyticsContext>): void {
        // Call base implementation to update internal context
        super.updateContext(context);

        // Update Sentry context when any relevant field changes
        const currentContext = this.getContext();
        if (
            context.domain !== undefined ||
            context.deploymentTarget !== undefined ||
            context.apiVersion !== undefined ||
            context.frontendVersion !== undefined ||
            context.environment !== undefined ||
            context.gitCommitSha !== undefined
        ) {
            setSentryAnalyticsContext(
                currentContext.domain,
                currentContext.deploymentTarget,
                currentContext.apiVersion,
                currentContext.frontendVersion,
                currentContext.environment,
                currentContext.gitCommitSha
            );
            logger.debug('✅ Sentry provider context updated:', {
                domain: currentContext.domain,
                deploymentTarget: currentContext.deploymentTarget,
                apiVersion: currentContext.apiVersion,
                frontendVersion: currentContext.frontendVersion,
                environment: currentContext.environment,
                gitCommitSha: currentContext.gitCommitSha,
            });
        }
    }

    protected async sendEvent(_event: AnalyticsEvent): Promise<void> {
        // No-op: This provider doesn't send events
        // It only maintains Sentry context
    }
}
